import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { hasRole, useAuthOptional } from '../lib/auth';
import { ActionButton, ErrorBox, Section, useApi } from './kit';
import { NotifyOwner } from './notify-owner';

// LinkedIn touches drafted for the person to send by hand (src/outbound/linkedin.ts), or sent from their own account
// once they connect it and turn auto-sending on for a campaign (src/outbound/linkedin-auto.ts). Each task has the person's profile (opens in a new tab), the drafted text with a copy button and the buttons for
// what happened (Sent, Accepted, Replied, Skip). Replied stops that lead's emails. A daily cap per person keeps the
// account safe; notes beyond it roll to the next day. On the LinkedIn tasks page, and on each campaign with it on.

type State = 'to_connect' | 'connect_sent' | 'accepted' | 'message_sent' | 'replied' | 'skipped';
export type LinkedinTask = {
  id: string; campaign: { id: string; name: string };
  person: { first_name: string | null; last_name: string | null; title: string | null; company: string; domain: string };
  state: State; linkedin_url: string | null; step: 'connect' | 'message' | null; text: string | null; note: string | null; message: string | null;
  sources: Array<{ kind: 'fact' | 'research' | 'signal'; text: string; source_url: string | null }>;
  due_on: string | null; rolled: boolean; skip_reason: string | null;
  auto?: boolean; auto_blocked?: string | null; reply_text?: string | null;
};
export type TaskList = {
  today: string; me: { daily_cap: number; suggested_cap: number; notes_sent_today: number; notes_left_today: number } | null;
  counts: { today: number; waiting: number; later: number; rolled: number; done: number };
  data: LinkedinTask[];
};
type Settings = { campaign_id: string; enabled: boolean; connect_day: number; message_days: number; sender_id: string; daily_cap: number; suggested_cap: number; counts: Record<State, number>; acceptance_rate: number | null };

const VIEWS = [['today', 'Today'], ['waiting', 'Waiting on them'], ['later', 'Later'], ['done', 'Done']] as const;
type View = (typeof VIEWS)[number][0];
const STATE_LABEL: Record<State, string> = {
  to_connect: 'Connection note to send', connect_sent: 'Note sent, waiting for acceptance', accepted: 'Accepted: message to send',
  message_sent: 'Message sent, waiting for a reply', replied: 'Replied', skipped: 'Skipped',
};
/** The buttons that apply to a task now (the server checks the same). */
const BUTTONS: Record<State, Array<'sent' | 'accepted' | 'replied' | 'skip'>> = {
  to_connect: ['sent', 'accepted', 'skip'], connect_sent: ['accepted', 'replied', 'skip'], accepted: ['sent', 'replied', 'skip'], message_sent: ['replied', 'skip'], replied: [], skipped: [],
};
const BUTTON_LABEL = { sent: 'Sent', accepted: 'Accepted', replied: 'Replied', skip: 'Skip' } as const;
const name = (p: LinkedinTask['person']) => [p.first_name, p.last_name].filter(Boolean).join(' ') || 'No name';
const day = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

function CopyButton({ text, what }: { text: string; what: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch { /* no clipboard (an old browser): the text is selectable */ }
    setDone(true);
    setTimeout(() => setDone(false), 2000);
  };
  return <button type="button" className="btn" onClick={() => void copy()} aria-label={`Copy the ${what}`}>{done ? 'Copied' : 'Copy'}</button>;
}

/** One task: who, their profile, the text to send now, what it rests on, and the buttons. */
function TaskItem({ t, canAct, showCampaign }: { t: LinkedinTask; canAct: boolean; showCampaign: boolean }) {
  const who = name(t.person);
  const what = t.step === 'message' ? 'message' : 'connection note';
  return (
    <li className="li-task">
      <div className="li-head">
        <div>
          <b>{who}</b>{t.person.title ? <span className="muted"> · {t.person.title}</span> : null} <span className="muted">at {t.person.company}</span>
          {showCampaign ? <div className="muted small">From <Link to={`/campaigns/${t.campaign.id}`}>“{t.campaign.name}”</Link></div> : null}
        </div>
        <span className={`chip${t.state === 'replied' ? ' done' : ''}`}>{STATE_LABEL[t.state]}</span>
      </div>
      {t.linkedin_url ? <p className="small"><a href={t.linkedin_url} target="_blank" rel="noopener noreferrer">Open {who}'s LinkedIn profile<span className="sr-only"> (opens in a new tab)</span></a></p> : null}
      {t.skip_reason ? <p className="muted small">{t.skip_reason}</p> : null}
      {t.auto && t.step ? <p className="small">Outlevo sends this from your LinkedIn account, within your limits.</p> : null}
      {t.auto_blocked ? <p className="small">Not sent automatically: {t.auto_blocked}</p> : null}
      {t.reply_text ? <blockquote className="small">{t.reply_text}</blockquote> : null}
      {t.text ? <>
        <div className="li-text-head"><span className="small">{t.step === 'message' ? 'Message to send' : 'Connection note to send'} <span className="muted">({t.text.length} characters{t.step === 'connect' ? ' of 300' : ''})</span></span><CopyButton text={t.text} what={what} /></div>
        <pre className="body li-text">{t.text}</pre>
      </> : null}
      {t.due_on && t.step ? <p className="muted small">{t.rolled ? `Moved to ${day(t.due_on)} by your daily cap` : `Due ${day(t.due_on)}`}</p> : null}
      {t.sources.length && t.text ? <details className="small"><summary>What it is based on</summary><ul className="tight">{t.sources.map((s, i) => (
        <li key={i}>{s.text}{s.source_url ? <> · <a href={s.source_url} target="_blank" rel="noopener noreferrer nofollow">source<span className="sr-only"> (opens in a new tab)</span></a></> : null}</li>
      ))}</ul></details> : null}
      {canAct && BUTTONS[t.state].length ? <div className="li-actions" role="group" aria-label={`What happened with ${who}`}>
        {BUTTONS[t.state].filter((a) => !t.auto || a === 'replied' || a === 'skip').map((a) => <ActionButton key={a} path={`/v1/linkedin/tasks/${t.id}/state`} body={{ action: a }} label={BUTTON_LABEL[a]} variant={a === 'sent' ? 'primary' : undefined}
          confirm={a === 'replied' ? `${who} replied on LinkedIn? Their emails stop.` : undefined} />)}
      </div> : null}
    </li>
  );
}

/** The reader's own pace: connection notes a day, the suggestion beside it. */
function DailyCap({ me }: { me: NonNullable<TaskList['me']> }) {
  const id = useId();
  const qc = useQueryClient();
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try { await api('/portal/linkedin/cap', { method: 'PUT', body: { daily_cap: Number(new FormData(e.currentTarget).get('cap')) } }); await qc.invalidateQueries(); } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  return (
    <form className="li-cap" onSubmit={save}>
      <p className="small">
        {me.notes_sent_today} of {me.daily_cap} connection notes sent today. To keep your LinkedIn account safe, Outlevo lists at most {me.daily_cap} a day ({me.suggested_cap} suggested); the rest roll to the next day.
      </p>
      <label htmlFor={id} className="small">Notes a day</label> <input id={id} name="cap" type="number" min={1} max={50} defaultValue={me.daily_cap} className="li-cap-input" />{' '}
      <button type="submit" className="btn" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      {err ? <ErrorBox error={err} doing="save the cap" /> : null}
    </form>
  );
}

/** The tasks list. campaignId: one campaign's (every sender); otherwise the reader's own, over all campaigns. */
export function LinkedinTasks({ campaignId }: { campaignId?: string }) {
  const [view, setView] = useState<View>('today');
  const me = useAuthOptional()?.me ?? null;
  const canAct = !me || hasRole(me, 'owner', 'admin', 'sales_approver', 'marketing_approver', 'rep', 'developer');
  const q = useApi<TaskList>(`/v1/linkedin/tasks?${new URLSearchParams({ view, ...(campaignId ? { campaign_id: campaignId } : { sender: 'me' }) })}`);
  if (q.error) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const d = q.data;
  return (
    <div className="li-tasks">
      <p className="muted small">Drafted by Outlevo, sent by you from your own LinkedIn, unless you turned on sending from your account for a campaign.</p>
      {d?.me && !campaignId ? <DailyCap me={d.me} /> : null}
      <div className="tabs" role="group" aria-label="Which tasks">
        {VIEWS.map(([v, l]) => <button key={v} type="button" className={`tab${v === view ? ' active' : ''}`} aria-pressed={v === view} onClick={() => setView(v)}>{l}{d ? ` (${d.counts[v]})` : ''}</button>)}
      </div>
      {!d ? <p className="muted" role="status">Loading…</p> : d.data.length ? (
        <ul className="li-list">{d.data.map((t) => <TaskItem key={t.id} t={t} canAct={canAct} showCampaign={!campaignId} />)}</ul>
      ) : <p className="muted">{view === 'today' ? (d.counts.later ? `Nothing to send today; ${d.counts.later} later${d.counts.rolled ? ` (${d.counts.rolled} moved by your daily cap)` : ''}.` : 'Nothing to send today.') : 'None.'}</p>}
    </div>
  );
}

/** The campaign page: LinkedIn touches on or off, their timing, and the campaign's tasks. */
export function CampaignLinkedin({ campaignId }: { campaignId: string }) {
  const q = useApi<Settings>(`/v1/campaigns/${campaignId}/linkedin`);
  const me = useAuthOptional()?.me ?? null;
  const canChange = !me || hasRole(me, 'owner', 'admin', 'sales_approver', 'marketing_approver', 'rep');
  if (q.error) return <ErrorBox error={q.error} />;
  const s = q.data;
  if (!s) return null;
  const sent = s.counts.connect_sent + s.counts.accepted + s.counts.message_sent + s.counts.replied;
  return (
    <Section title="LinkedIn touches" actions={canChange ? <ActionButton path={`/v1/campaigns/${campaignId}/linkedin`} method="PUT" body={{ enabled: !s.enabled }} label={s.enabled ? 'Turn off' : 'Turn on'}
      confirm={s.enabled ? undefined : 'Draft a LinkedIn connection note and one follow-up for each contact, for you to send yourself?'} /> : null}>
      {s.enabled ? <>
        <p className="muted small">
          A connection note {s.connect_day} day{s.connect_day === 1 ? '' : 's'} after each contact's first email, and one message {s.message_days} day{s.message_days === 1 ? '' : 's'} after you mark it accepted,
          drafted from the same angle and research as the email, for {me && s.sender_id === me.user_id ? 'you' : 'the sender'} to send by hand (up to {s.daily_cap} notes a day). Contacts without a LinkedIn profile on record are skipped.
          {sent ? ` ${sent} sent so far${s.acceptance_rate !== null ? `, ${Math.round(s.acceptance_rate * 100)}% accepted` : ''}, ${s.counts.replied} replied.` : ''}
        </p>
        <AutoSend campaignId={campaignId} />
        <LinkedinTasks campaignId={campaignId} />
      </> : <p className="muted small">Off. Turned on, Outlevo drafts a LinkedIn connection note (300 characters at most, no links) and one follow-up per contact, from the same angle and research as the email, as tasks for you to send yourself from your own LinkedIn. Nothing is sent for you unless you turn that on separately, for your own account.</p>}
    </Section>
  );
}

// Sending from the person's own LinkedIn account (src/outbound/linkedin-auto.ts) --------------------------------------

type AccountStatus = 'pending' | 'ok' | 'credentials' | 'restricted' | 'error' | 'disconnected';
type AccountView = {
  available: boolean; plan_allows: boolean | null; plan_note: string | null;
  account: null | {
    id: string; status: AccountStatus; status_detail: string | null; paused: boolean; paused_reason: string | null; timezone: string;
    limits: { daily_invites: number; daily_messages: number; weekly_invites: number; work_start: number; work_end: number; weekends: boolean };
    today: { invitations: number; messages: number; invitations_week: number; invitations_held: boolean };
  };
  max: { dailyInvites: number; dailyMessages: number; weeklyInvites: number };
};
const ACCOUNT_STATUS: Record<AccountStatus, string> = {
  pending: 'Waiting for the LinkedIn sign-in', ok: 'Connected', credentials: 'LinkedIn asks you to sign in again', restricted: 'Restricted by LinkedIn',
  error: 'Connection stopped', disconnected: 'Disconnected',
};

/** The reader's own LinkedIn account: connect it on Unipile's page, its state, the limits (lower only), disconnect. */
export function LinkedinAccount() {
  const q = useApi<AccountView>('/portal/linkedin/account');
  const me = useAuthOptional()?.me ?? null;
  const owner = !me || hasRole(me, 'owner');
  const qc = useQueryClient();
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const id = useId();
  if (q.error) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const v = q.data;
  if (!v) return null;
  if (!v.available) return <p className="muted small">Sending from your own LinkedIn account is not available yet. Your LinkedIn tasks are drafted for you to send by hand.</p>;
  // the way on is Billing: no Connect button without the plan (the page said "press Connect LinkedIn" and had none)
  if (v.plan_allows === false) return <p className="muted small">{v.plan_note ?? 'Your plan does not include sending from your LinkedIn account.'} Until then, your LinkedIn tasks are drafted for you to send by hand. {owner ? <Link to="/billing#plan">See plans</Link> : <NotifyOwner need="plan" compact what="Only the workspace owner can change the plan." />}</p>;
  const go = (r: { url?: string }) => { if (r.url) location.assign(r.url); };
  const a = v.account;
  if (!a) {
    return (
      <div className="li-account">
        <p className="small">Connect your own LinkedIn account to let Outlevo send the invitations and follow-ups of campaigns where you turn it on. You sign in on Unipile&apos;s page; Outlevo never sees or stores your LinkedIn password.</p>
        <ActionButton path="/portal/linkedin/account/connect" label="Connect LinkedIn" variant="primary" onDone={go} />
      </div>
    );
  }
  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr(null); setBusy(true);
    const f = new FormData(e.currentTarget);
    try {
      await api('/portal/linkedin/account/limits', { method: 'PUT', body: { daily_invites: Number(f.get('daily_invites')), daily_messages: Number(f.get('daily_messages')), weekly_invites: Number(f.get('weekly_invites')) } });
      await qc.invalidateQueries();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  return (
    <div className="li-account">
      <p className="small"><b>{ACCOUNT_STATUS[a.status]}</b>{a.paused ? <> · Outlevo stopped sending: {a.paused_reason}. Nothing is sent until you reconnect or resume.</> : null}</p>
      <p className="muted small">Today: {a.today.invitations} of {a.limits.daily_invites} invitations and {a.today.messages} of {a.limits.daily_messages} messages; {a.today.invitations_week} of {a.limits.weekly_invites} invitations this week{a.today.invitations_held ? ' (invitations paused for a few days: LinkedIn’s limit was reached)' : ''}. Weekdays {a.limits.work_start}:00 to {a.limits.work_end}:00 ({a.timezone}), with random gaps between actions.</p>
      <div className="li-actions">
        {a.paused || a.status !== 'ok' ? <ActionButton path="/portal/linkedin/account/connect" label="Reconnect" variant="primary" onDone={go} /> : null}
        {a.paused ? <ActionButton path="/portal/linkedin/account/resume" label="Resume" /> : null}
        <ActionButton path="/portal/linkedin/account" method="DELETE" label="Disconnect" variant="danger" confirm="Disconnect your LinkedIn account? Automatic sending stops on your campaigns; the tasks stay for you to send by hand." />
      </div>
      <form className="li-cap" onSubmit={save} aria-label="Your LinkedIn limits">
        <p className="small">Your limits (you can only lower them):</p>
        <label htmlFor={`${id}-i`} className="small">Invitations a day</label> <input id={`${id}-i`} name="daily_invites" type="number" min={1} max={v.max.dailyInvites} defaultValue={a.limits.daily_invites} className="li-cap-input" />{' '}
        <label htmlFor={`${id}-m`} className="small">Messages a day</label> <input id={`${id}-m`} name="daily_messages" type="number" min={1} max={v.max.dailyMessages} defaultValue={a.limits.daily_messages} className="li-cap-input" />{' '}
        <label htmlFor={`${id}-w`} className="small">Invitations a week</label> <input id={`${id}-w`} name="weekly_invites" type="number" min={1} max={v.max.weeklyInvites} defaultValue={a.limits.weekly_invites} className="li-cap-input" />{' '}
        <button type="submit" className="btn" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        {err ? <ErrorBox error={err} doing="save the limits" /> : null}
      </form>
    </div>
  );
}

type Auto = {
  available: boolean; enabled: boolean; sender_id: string; mine: boolean; account_status: AccountStatus | null; account_paused: boolean;
  consent: { version: string; text: string; accepted_at: string | null; accepted_version: string | null };
  sent: { invitations: number; messages: number };
};

/** A campaign's automatic sending: on only by the sender, after reading and accepting the warning. */
export function AutoSend({ campaignId }: { campaignId: string }) {
  const q = useApi<Auto>(`/v1/campaigns/${campaignId}/linkedin/auto`);
  const qc = useQueryClient();
  const me = useAuthOptional()?.me ?? null;
  const canChange = !me || hasRole(me, 'owner', 'admin', 'sales_approver', 'marketing_approver', 'rep');
  const [asking, setAsking] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const boxId = useId();
  if (q.error) return <ErrorBox error={q.error} />;
  const a = q.data;
  if (!a) return null;
  if (!a.available) return <p className="muted small">Sending these from your LinkedIn account is not available yet.</p>;
  if (a.enabled) {
    return (
      <div className="li-auto">
        <p className="small"><b>Sent automatically</b> from {a.mine ? 'your' : 'the sender’s'} LinkedIn account, within the account&apos;s limits{a.account_paused ? ' (paused: the account needs attention on the LinkedIn tasks page)' : ''}. {a.sent.invitations} invitations and {a.sent.messages} messages sent so far.</p>
        {canChange ? <ActionButton path={`/v1/campaigns/${campaignId}/linkedin/auto`} method="PUT" body={{ enabled: false }} label="Stop sending automatically" /> : null}
      </div>
    );
  }
  if (!a.mine) return <p className="muted small">Only this campaign&apos;s LinkedIn sender can have Outlevo send these from their own account.</p>;
  if (a.account_status !== 'ok') return <p className="muted small">To have Outlevo send these from your LinkedIn account, first <Link to="/linkedin">connect your LinkedIn account</Link>.</p>;
  const turnOn = async () => {
    setErr(null); setBusy(true);
    try {
      await api(`/v1/campaigns/${campaignId}/linkedin/auto`, { method: 'PUT', body: { enabled: true, consent_version: a.consent.version, accept: true } });
      setAsking(false); await qc.invalidateQueries();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  if (!asking) return <p className="small"><button type="button" className="btn" onClick={() => setAsking(true)}>Send these automatically…</button></p>;
  return (
    <div className="li-consent" role="group" aria-label="Before Outlevo sends from your LinkedIn account">
      <h3 className="small">Before Outlevo sends from your LinkedIn account</h3>
      {a.consent.text.split('\n\n').map((p, i) => <p key={i} className="small">{p}</p>)}
      <p className="small"><input id={boxId} type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} /> <label htmlFor={boxId}>I have read this and accept it</label></p>
      <div className="li-actions">
        <button type="button" className="btn btn-primary" disabled={!accepted || busy} onClick={() => void turnOn()}>{busy ? 'Working…' : 'Turn on'}</button>
        <button type="button" className="btn" onClick={() => { setAsking(false); setAccepted(false); }}>Cancel</button>
      </div>
      {err ? <ErrorBox error={err} doing="turn it on" /> : null}
    </div>
  );
}
